import type { ChatMessage, GenerationOptions } from '../clients';
import { getAiClient, getDefaultOptions } from '../config';

export interface StructuredGenerationOptions {
  systemPrompt: string;
  userPrompt: string;
  temperature?: number;
  maxTokens?: number;
}

export class AIService {
  private async safeCall(
    messages: ChatMessage[],
    options?: GenerationOptions,
    fallback = ''
  ): Promise<string> {
    try {
      const client = getAiClient();
      const mergedOptions = { ...getDefaultOptions(), ...options };
      const response = await client.chat(messages, mergedOptions);
      return response || fallback;
    } catch (err) {
      // Swallowed errors leave a breadcrumb for SRE — the caller path
      // (`generateText`) falls back to the empty string.
      console.warn(
        '[ai-service] safeCall swallowed error:',
        err instanceof Error ? err.message : String(err)
      );
      return fallback;
    }
  }

  private cleanText(text: string | undefined): string {
    if (!text) return '';
    let cleaned = text.trim();
    if (
      (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
      (cleaned.startsWith("'") && cleaned.endsWith("'"))
    ) {
      cleaned = cleaned.slice(1, -1).trim();
    }
    return cleaned;
  }

  private extractJson(text: string): string {
    let cleaned = text
      .replace(/```json\n?/g, '')
      .replace(/```\n?/g, '')
      .trim();

    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      cleaned = jsonMatch[0];
    }

    cleaned = cleaned.replace(/,\s*(\]|\})/g, '$1');
    cleaned = cleaned.replace(/\n/g, ' ').replace(/\s+/g, ' ');

    return cleaned;
  }

  async generateText(
    prompt: string,
    options?: GenerationOptions
  ): Promise<string> {
    const messages: ChatMessage[] = [{ role: 'user', content: prompt }];
    const result = await this.safeCall(messages, options);
    return this.cleanText(result);
  }

  /**
   * Like `generateText`, but a failed provider call or an empty reply throws
   * instead of degrading to '' — for callers that must not mistake an outage
   * for an answer.
   */
  async generateTextStrict(
    prompt: string,
    options?: GenerationOptions
  ): Promise<string> {
    const client = getAiClient();
    const response = await client.chat([{ role: 'user', content: prompt }], {
      ...getDefaultOptions(),
      ...options,
    });
    const text = this.cleanText(response);
    if (!text) throw new Error('Empty response from AI service');
    return text;
  }

  async generateStructuredContent<T>(
    options: StructuredGenerationOptions
  ): Promise<T> {
    const { systemPrompt, userPrompt, temperature, maxTokens } = options;

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ];

    const generationOptions: GenerationOptions = {
      temperature: temperature ?? 0.3,
      maxTokens: maxTokens ?? 4000,
    };

    let response: string;
    try {
      const client = getAiClient();
      const mergedOptions = { ...getDefaultOptions(), ...generationOptions };
      response = await client.chat(messages, mergedOptions);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`AI service failed: ${errorMessage}`);
    }

    if (!response || response.trim() === '') {
      throw new Error('Empty response from AI service');
    }

    try {
      const jsonText = this.extractJson(response);
      const parsed = JSON.parse(jsonText);
      return parsed as T;
    } catch (error) {
      throw new Error(
        `Failed to parse structured content: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

export const aiService = new AIService();
